import { Pill, Bell, AlertCircle, Check, X, Clock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

interface NotificationItemProps {
  id: string
  type: 'dose_reminder' | 'dose_update' | 'notification' | 'error'
  title: string
  body: string
  timestamp: Date
  read: boolean
  onMarkAsRead?: (id: string) => void
  onDismiss?: (id: string) => void
}

const typeConfig: Record<NotificationItemProps['type'], { icon: LucideIcon; color: string }> = {
  dose_reminder: { icon: Pill, color: 'var(--color-primary)' },
  dose_update: { icon: Check, color: 'var(--color-success)' },
  notification: { icon: Bell, color: 'var(--color-info)' },
  error: { icon: AlertCircle, color: 'var(--color-danger)' },
}

export function NotificationItem({
  id,
  type,
  title,
  body,
  timestamp,
  read,
  onMarkAsRead,
  onDismiss,
}: NotificationItemProps) {
  const config = typeConfig[type]
  const Icon = config.icon

  const timeAgo = formatTimeAgo(timestamp)

  return (
    <div
      className={`notification-item ${read ? 'notification-item--read' : ''}`}
      onClick={() => !read && onMarkAsRead?.(id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && !read && onMarkAsRead?.(id)}
    >
      <div className="notification-item__icon" style={{ color: config.color }}>
        <Icon size={20} />
      </div>

      <div className="notification-item__content">
        <div className="notification-item__header">
          <span className="notification-item__title">{title}</span>
          <span className="notification-item__time">
            <Clock size={12} />
            {timeAgo}
          </span>
        </div>
        <p className="notification-item__body">{body}</p>
      </div>

      {!read && (
        <div className="notification-item__indicator" />
      )}

      {onDismiss && (
        <button
          className="notification-item__dismiss"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss(id)
          }}
          aria-label="Descartar"
        >
          <X size={14} />
        </button>
      )}

      <style>{`
        .notification-item {
          display: flex;
          align-items: flex-start;
          gap: var(--spacing-3);
          padding: var(--spacing-3);
          border-radius: var(--radius-md);
          background: var(--color-bg-secondary);
          cursor: pointer;
          transition: background-color 0.2s;
          position: relative;
        }

        .notification-item:hover {
          background: var(--color-bg-tertiary);
        }

        .notification-item--read {
          opacity: 0.7;
        }

        .notification-item__icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--radius-full);
          background: var(--color-bg-primary);
        }

        .notification-item__content {
          flex: 1;
          min-width: 0;
        }

        .notification-item__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--spacing-2);
          margin-bottom: var(--spacing-1);
        }

        .notification-item__title {
          font-weight: 500;
          font-size: var(--font-size-sm);
          color: var(--color-text-primary);
        }

        .notification-item__time {
          display: flex;
          align-items: center;
          gap: var(--spacing-1);
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          flex-shrink: 0;
        }

        .notification-item__body {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
          margin: 0;
          line-height: 1.4;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .notification-item__indicator {
          position: absolute;
          top: var(--spacing-3);
          right: var(--spacing-3);
          width: 8px;
          height: 8px;
          border-radius: var(--radius-full);
          background: var(--color-primary);
        }

        .notification-item__dismiss {
          position: absolute;
          top: var(--spacing-2);
          right: var(--spacing-2);
          display: none;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          padding: 0;
          border: none;
          border-radius: var(--radius-full);
          background: var(--color-bg-tertiary);
          color: var(--color-text-tertiary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .notification-item:hover .notification-item__dismiss {
          display: flex;
        }

        .notification-item:hover .notification-item__indicator {
          display: none;
        }

        .notification-item__dismiss:hover {
          background: var(--color-danger-bg);
          color: var(--color-danger);
        }
      `}</style>
    </div>
  )
}

function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'ahora'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`

  return date.toLocaleDateString('es', { month: 'short', day: 'numeric' })
}
